import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  ShoppingBag,
  Users as UsersIcon,
  Shield,
  Wrench,
  BarChart3,
  Menu,
  X,
  Settings,
  ChevronRight,
  LogOut,
  Smartphone,
  UserCog,
  Wind,
  Store,
  ChevronDown
} from 'lucide-react';

// Pages
import Dashboard from './pages/Dashboard';
import Sales from './pages/Sales';
import Purchases from './pages/Purchases';
import Klientet from './pages/Klientet';
import Services from './pages/Services';
import Reports from './pages/Reports';
import SettingsPage from './pages/Settings';
import Admin from './pages/Admin';
import AirConditioners from './pages/AirConditioners';
import Magazina from './pages/Magazina';
import Login from './pages/Login';
import { api } from './lib/utils';
import { StoreProvider, useStore } from './context/StoreContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Navigation items with role access and store-specific modules
// super_admin: only Dashboard, Reports, Admin (control only, no operations)
// store_admin: all store operations
// seller: sales and customers only
const navItems = [
  { path: '/', label: 'Paneli', icon: LayoutDashboard, roles: ['admin', 'super_admin', 'store_admin', 'seller'], allStores: true },
  { path: '/sales', label: 'Shitjet', icon: ShoppingCart, roles: ['seller'], allStores: true, module: 'sales' },
  { path: '/magazina', label: 'Magazina', icon: Package, roles: ['admin', 'store_admin'], allStores: true, module: 'magazina' },
  { path: '/klientet', label: 'Klientët', icon: UsersIcon, roles: ['admin', 'store_admin', 'seller'], allStores: true, module: 'klientet' },
  { path: '/services', label: 'Serviset', icon: Wrench, roles: ['admin', 'store_admin', 'seller'], allStores: false, module: 'services', storeTypes: ['tech'] },
  { path: '/ac', label: 'Kondicionerë', icon: Wind, roles: ['admin', 'store_admin', 'seller'], allStores: false, module: 'ac', storeTypes: ['tech'] },
  { path: '/purchases', label: 'Blerjet', icon: ShoppingBag, roles: ['admin', 'store_admin'], allStores: true, module: 'purchases' },
  { path: '/reports', label: 'Raportet', icon: BarChart3, roles: ['admin', 'super_admin', 'store_admin'], allStores: true, module: 'reports' },
  { path: '/admin', label: 'Admin', icon: UserCog, roles: ['super_admin'], allStores: true },
  { path: '/settings', label: 'Cilësimet', icon: Settings, roles: ['admin', 'super_admin', 'store_admin'], allStores: true },
];

function Sidebar({ isOpen, setIsOpen, user, onLogout, activeStore }) {
  const location = useLocation();
  const { currentStore, stores, canSwitchStores, switchStore, loading: storeLoading } = useStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const displayStore = currentStore || activeStore;

  // Filter nav items based on role, overrides, and store modules
  const visibleNavItems = navItems.filter(item => {
    // 1. Check Custom Permission Overrides (e.g., 'seller' given access to 'magazina')
    let hasAccess = false;

    if (user?.custom_permissions && user.custom_permissions.length > 0 && item.module) {
      if (user.custom_permissions.includes(item.module)) {
        // Explicitly granted via custom permission
        hasAccess = true;
      }
    }

    // If no custom permission granted, fallback to role default
    if (!hasAccess) {
      if (item.roles.includes(user?.role || 'seller')) {
        hasAccess = true;
      }
    }

    // If somehow denied by both, hide it
    if (!hasAccess) return false;

    // 2. Super Admin "All Stores" View (Global Mode)
    // If no specific store is selected, HIDE operational menus (Dashboard, Reports, etc.)
    // Show only: Admin, Settings
    if (canSwitchStores && !currentStore) {
      if (['/admin', '/settings'].includes(item.path)) {
        return true;
      }
      return false;
    }

    const storeToCheck = currentStore || activeStore;

    // 3. Store Type Check
    if (item.storeTypes && storeToCheck?.type) {
      if (!item.storeTypes.includes(storeToCheck.type)) {
        return false;
      }
    }

    // 4. Store-Specific Module Access
    if (storeToCheck && item.module) {
      // If store has defined modules, check if this module is enabled
      if (storeToCheck.modules && Array.isArray(storeToCheck.modules)) {
        if (!storeToCheck.modules.includes(item.module)) {
          return false;
        }
      } else {
        // Fallback for stores created before modules were added
        if (['services', 'ac'].includes(item.module)) return false;
      }
    }

    return true;
  });

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200
        transform transition-transform duration-300 ease-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-auto
      `}>
        {/* Logo - Shows store logo or Admin badge */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 bg-primary">
          <div className="flex items-center gap-3">
            {isAdmin && !currentStore ? (
              <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
            ) : displayStore?.logo_url ? (
              <img src={displayStore.logo_url} alt={displayStore.name} className="w-9 h-9 rounded-lg object-contain bg-white p-1" />
            ) : (
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                <Store className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              {isAdmin && !currentStore ? (
                <>
                  <span className="font-bold text-white text-lg">SUPER ADMIN</span>
                  <span className="block text-white/80 text-xs -mt-1">Të gjithë dyqanet</span>
                </>
              ) : (
                <>
                  <span className="font-bold text-white text-lg">{displayStore?.name || 'DYQANI'}</span>
                  {displayStore?.address && <span className="block text-white/80 text-xs -mt-1 truncate max-w-[120px]">{displayStore.address}</span>}
                </>
              )}
            </div>
          </div>
          <button
            className="lg:hidden p-2 hover:bg-white/10 rounded-md text-white"
            onClick={() => setIsOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Store Selector - Only for Super Admin */}
        {canSwitchStores && stores.length > 0 && (
          <div className="p-3 border-b border-slate-200 bg-slate-50">
            <label className="text-xs text-slate-500 font-medium mb-1 block">Zgjidhni Dyqanin</label>
            <select
              value={currentStore?.id || ''}
              onChange={(e) => switchStore(e.target.value || null)}
              className="w-full p-2 text-sm border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">Të gjithë dyqanet</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto pb-24" style={{ maxHeight: canSwitchStores ? 'calc(100vh - 200px)' : 'calc(100vh - 140px)' }}>
          {visibleNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm
                  transition-all duration-200
                  ${isActive
                    ? 'bg-primary text-white shadow-md shadow-primary/25'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }
                `}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* User & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isAdmin ? 'bg-amber-100' : 'bg-primary/10'}`}>
                {isAdmin ? <Shield className="w-4 h-4 text-amber-600" /> : <UsersIcon className="w-4 h-4 text-primary" />}
              </div>
              <div className="text-sm">
                <p className="font-medium text-slate-900">{user?.name || user?.username}</p>
                <p className="text-slate-500 text-xs">{isAdmin ? 'Administrator' : 'Shitës'}</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="p-2 hover:bg-slate-100 rounded-md text-slate-500 hover:text-primary"
              title="Dilni"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

function Header({ setIsOpen }) {
  const location = useLocation();
  const currentPage = navItems.find(item => item.path === location.pathname);

  return (
    <header className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-200/50 px-4 lg:px-8 flex items-center justify-between sticky top-0 z-30">
      <div className="flex items-center gap-4">
        <button
          className="lg:hidden p-2 hover:bg-slate-100 rounded-md"
          onClick={() => setIsOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {currentPage?.label || 'Paneli'}
          </h1>
        </div>
      </div>
    </header>
  );
}

function Layout({ children, user, onLogout, activeStore }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} user={user} onLogout={onLogout} activeStore={activeStore} />
      <div className="flex-1 flex flex-col min-w-0">
        <Header setIsOpen={setSidebarOpen} />
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStore, setActiveStore] = useState(null);

  const loadActiveStore = async (userObj = null) => {
    try {
      const currentUser = userObj || JSON.parse(localStorage.getItem('user') || '{}');
      // If user has a store_id assigned, load that store
      if (currentUser?.store_id) {
        const response = await api.get(`/stores/${currentUser.store_id}`);
        setActiveStore(response.data);
      } else {
        // Otherwise load the default active store
        const response = await api.get('/stores/active/current');
        setActiveStore(response.data);
      }
    } catch (error) {
      console.error('Error loading active store:', error);
    }
  };

  useEffect(() => {
    // Check for existing token
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(JSON.parse(savedUser));
      loadActiveStore();
    }
    setLoading(false);

    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(console.error);
    }
  }, []);

  const handleLogin = (userData) => {
    const token = localStorage.getItem('token');
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    loadActiveStore(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedStoreId');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Ju dolët me sukses');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Toaster position="top-right" richColors />
        <Login onLogin={handleLogin} />
      </>
    );
  }

  return (
    <BrowserRouter>
      <StoreProvider>
        <Toaster position="top-right" richColors />
        <Layout user={user} onLogout={handleLogout} activeStore={activeStore}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/magazina" element={<Magazina />} />
            <Route path="/sales" element={<Sales />} />
            <Route path="/purchases" element={<Purchases />} />
            <Route path="/klientet" element={<Klientet />} />
            <Route path="/services" element={<Services />} />
            <Route path="/ac" element={<AirConditioners />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </StoreProvider>
    </BrowserRouter>
  );
}

export default App;
